import {
  StudyFigureCut,
  StudyFigureFilter,
  StudyFigureGroup,
  StudyFigureNote,
  StudyFigureQuestion,
  StudyFiguresRequest,
  StudyFiguresView,
} from '@digital-twins/shared-types'

/**
 * What one card asks the figure service for, besides its question: the cut it
 * splits by and the filters that narrow the sample. Cards that ask the same
 * shape share one request (plots plan D2: one batched request per tab).
 */
export interface FigureRequestShape {
  cut?: StudyFigureCut | null
  filters?: Array<StudyFigureFilter>
}

/**
 * Why a card has no figure to draw: the report was regenerated since the page
 * loaded (409), the service refused the cut (400, final: asking again cannot
 * change the answer), or the request failed (retryable).
 */
export type FigureFailure = 'stale_report' | 'rejected' | 'failed'

export interface FigureLoadingEntry {
  status: 'loading'
}

export interface FigureReadyEntry {
  status: 'ready'
  /** Null when the service drew no figure for this question (not plotted, or dropped). */
  question: StudyFigureQuestion | null
  /** Total first, then the cut's groups in cut order. */
  groups: Array<StudyFigureGroup>
  /** The notes that concern this card: its own `question_invalid` and the request-wide ones. */
  notes: Array<StudyFigureNote>
}

export interface FigureErrorEntry {
  status: 'error'
  failure: FigureFailure
  /** The service's typed refusal reason (e.g. `unknown_split`) when it gave one. */
  code?: string
}

export type FigureEntry = FigureLoadingEntry | FigureReadyEntry | FigureErrorEntry

export type FetchStudyFigures = (request: StudyFiguresRequest) => Promise<StudyFiguresView>

export interface FigureBatcherOptions {
  fetchFigures: FetchStudyFigures
  /** The viewer's UI language (decision 5): plot chrome resolves in it. */
  locale?: string
  /**
   * The displayed report's generation time (the deck's `createdAt`); the
   * service answers 409 when the report changed since. Omitted with no deck.
   */
  reportRevision?: string
  /** Runs a flush after the current commit; injectable for tests. */
  schedule?: (flush: () => void) => void
  /**
   * Called once, on the first 409: the report changed since the list of cuts
   * was read. The source re-reads it and, on a new revision, builds a new
   * batcher, so the cards redraw without a reload.
   */
  onStaleReport?: () => void
}

export interface FigureBatcher {
  /** The entry for one question under one shape; the same object until it changes. */
  get: (questionId: string, shape: FigureRequestShape) => FigureEntry
  /** Asks for a figure; questions asked in the same tick share one request per shape. */
  request: (questionId: string, shape: FigureRequestShape) => void
  /** Drops retryable failed entries for one shape and asks for them again. */
  retry: (shape: FigureRequestShape) => void
  subscribe: (listener: () => void) => () => void
}

const LOADING: FigureLoadingEntry = { status: 'loading' }

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(',')}]`
  }
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return `{${Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

/** One request per distinct (cut, filters): the batching key. */
export function figureShapeKey(shape: FigureRequestShape): string {
  return canonicalJson({ cut: shape.cut ?? null, filters: shape.filters ?? [] })
}

function readFailure(error: unknown): FigureFailure {
  const status = (error as { response?: { status?: number } } | null)?.response?.status
  if (status === 409) {
    return 'stale_report'
  }
  if (status === 400) {
    return 'rejected'
  }
  return 'failed'
}

/** A typed refusal reason: snake_case, short, nothing free-text. */
const REFUSAL_CODE_PATTERN = /^[a-z][a-z0-9_]{0,63}$/

function readRefusalCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code
  if (typeof code !== 'string' || !REFUSAL_CODE_PATTERN.test(code)) {
    return null
  }
  return code
}

function notesFor(view: StudyFiguresView, questionId: string): Array<StudyFigureNote> {
  return view.notes.filter((note) => note.questionId == null || note.questionId === questionId)
}

function defaultSchedule(flush: () => void): void {
  setTimeout(flush, 0)
}

/**
 * The survey tab's figure client (plots plan D3). Cards ask for one question
 * each; every question asked in the same tick under the same shape goes out as
 * ONE request with the union of question ids. Entries are cached per shape and
 * question for the tab's lifetime (Nest caches across viewers). No arithmetic:
 * the view is stored as Python returned it.
 */
export function createFigureBatcher(options: FigureBatcherOptions): FigureBatcher {
  const { fetchFigures, locale, reportRevision, onStaleReport } = options
  const schedule = options.schedule ?? defaultSchedule
  let hasReportedStale = false
  const entries = new Map<string, Map<string, FigureEntry>>()
  const pending = new Map<string, { shape: FigureRequestShape; questionIds: Set<string> }>()
  const listeners = new Set<() => void>()
  let isFlushScheduled = false

  function notify(): void {
    listeners.forEach((listener) => listener())
  }

  function entriesFor(key: string): Map<string, FigureEntry> {
    let byQuestion = entries.get(key)
    if (!byQuestion) {
      byQuestion = new Map()
      entries.set(key, byQuestion)
    }
    return byQuestion
  }

  function send(key: string, shape: FigureRequestShape, questionIds: Array<string>): void {
    const request: StudyFiguresRequest = {
      questionIds,
      ...(shape.cut ? { cut: shape.cut } : {}),
      ...(shape.filters && shape.filters.length > 0 ? { filters: shape.filters } : {}),
      ...(locale ? { locale } : {}),
      ...(reportRevision ? { reportRevision } : {}),
    }
    fetchFigures(request).then(
      (view) => {
        const byQuestion = entriesFor(key)
        questionIds.forEach((questionId) => {
          byQuestion.set(questionId, {
            status: 'ready',
            question: view.questions.find((question) => question.questionId === questionId) ?? null,
            groups: view.groups,
            notes: notesFor(view, questionId),
          })
        })
        notify()
      },
      (error: unknown) => {
        const failure = readFailure(error)
        const code = readRefusalCode(error)
        const entry: FigureErrorEntry = code ? { status: 'error', failure, code } : { status: 'error', failure }
        const byQuestion = entriesFor(key)
        questionIds.forEach((questionId) => {
          byQuestion.set(questionId, entry)
        })
        notify()
        if (failure === 'stale_report' && onStaleReport && !hasReportedStale) {
          hasReportedStale = true
          onStaleReport()
        }
      },
    )
  }

  function flush(): void {
    isFlushScheduled = false
    const batches = [...pending.entries()]
    pending.clear()
    batches.forEach(([key, batch]) => {
      send(key, batch.shape, [...batch.questionIds].sort())
    })
  }

  function request(questionId: string, shape: FigureRequestShape): void {
    const key = figureShapeKey(shape)
    const byQuestion = entriesFor(key)
    if (byQuestion.has(questionId)) {
      return
    }
    byQuestion.set(questionId, LOADING)
    const batch = pending.get(key) ?? { shape, questionIds: new Set<string>() }
    batch.questionIds.add(questionId)
    pending.set(key, batch)
    if (!isFlushScheduled) {
      isFlushScheduled = true
      schedule(flush)
    }
  }

  function get(questionId: string, shape: FigureRequestShape): FigureEntry {
    return entries.get(figureShapeKey(shape))?.get(questionId) ?? LOADING
  }

  function retry(shape: FigureRequestShape): void {
    const key = figureShapeKey(shape)
    const byQuestion = entries.get(key)
    if (!byQuestion) {
      return
    }
    const failed = [...byQuestion.entries()]
      .filter(([, entry]) => entry.status === 'error' && entry.failure === 'failed')
      .map(([questionId]) => questionId)
    failed.forEach((questionId) => byQuestion.delete(questionId))
    failed.forEach((questionId) => request(questionId, shape))
    notify()
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  return { get, request, retry, subscribe }
}
